import {
  DEFAULT_CHART_DATA,
  DEFAULT_TABLE_DATA,
  DEFAULT_TOTAL_COMPANY,
} from './slice'

export const RATIO_COL_ID = 'ratio'
export const RATIO_INDICATOR_COL_ID = 'ratioIndicator'
export const LINE_KEY = 'lineKey'
export const AREA_KEY = 'areaKey'

const ratio = [
  {
    title: 'portfolio.finance.RATIO',
    colId: RATIO_COL_ID,
    isI18n: true,
  },
]
const ratioIndicator = {
  title: 'portfolio.finance.PORTFOLIO',
  colId: RATIO_INDICATOR_COL_ID,
  isI18n: true,
}

export const handleReportYear = (payload) => {
  if (!Array.isArray(payload)) return new Date().getFullYear()

  return Math.max.apply(Math, payload)
}

export const handleTotalCompany = (payload, displayType) => {
  if (!Array.isArray(payload)) return DEFAULT_TOTAL_COMPANY

  const totalCompanyInfo = payload.find(
    (item) => item.companyType === displayType,
  )
  return totalCompanyInfo
    ? {
        totalCompany: totalCompanyInfo.totalCompanies,
        totalCount: totalCompanyInfo.totalCount,
      }
    : DEFAULT_TOTAL_COMPANY
}

export const getKeyByTypePrefix = (typePrefix) => {
  const lastIndexSlash = typePrefix.lastIndexOf('/')
  return typePrefix.slice(lastIndexSlash + 1)
}

export const handleTableData = (payload, args) => {
  if (!Array.isArray(payload) || !payload.length) return DEFAULT_TABLE_DATA

  const [filterPortfolio] = payload
  const tableHeader = getTableHeader(filterPortfolio, args)
  const dataByRowId = groupByRowId(payload)

  return { tableHeader, dataByRowId, ids: Object.keys(dataByRowId) }
}

const getTableHeader = (filterPortfolio, args) => {
  const { finance_RatioDataItem } = filterPortfolio

  if (!finance_RatioDataItem.length) return []

  const [firstItem] = finance_RatioDataItem
  const { portfolioId1, portfolioId2 } = args

  const fixedColumns = [...ratio]
  if (portfolioId1 || portfolioId2) {
    fixedColumns.push(ratioIndicator)
  }

  if (!firstItem.values) return []

  const columns = firstItem.values
    .map((item) => ({
      title: item.realYear,
      colId: item.realYear,
      thStyle: { justifyContent: 'center' },
    }))
    .sort((a, b) => a.colId - b.colId)

  return [...fixedColumns, ...columns]
}

const groupByRowId = (tableData) => {
  const [filterPortfolio] = tableData

  if (!Object.keys(filterPortfolio)) return {}
  const { finance_RatioDataItem: ratioData } = filterPortfolio
  const result = {}

  ratioData.forEach((item) => {
    const groupRowId = item.ratioCode
    result[groupRowId] = handleGroupRowData(tableData, groupRowId)
  })

  return result
}

const handleGroupRowData = (tableData, groupRowId) => {
  const result = []
  tableData.forEach(
    ({ finance_RatioDataItem: ratioData, name: ratioIndicator }) => {
      const currentRatio = ratioData.find(
        (item) => item.ratioCode === groupRowId,
      )
      if (!currentRatio) return

      const { ratioName, values, unit } = currentRatio
      const medianRow = getMedianRow(values, ratioName, unit, ratioIndicator)
      const minMaxRow = getMinMaxRow(values, unit)

      result.push(medianRow, minMaxRow)
    },
  )

  return result
}

const getMedianRow = (values, ratioName, unit, ratioIndicator) => {
  return values.reduce(
    (prev, cur) => {
      return {
        ...prev,
        [cur.realYear]: unit === '%' ? cur.median * 100 : cur.median,
      }
    },
    { ratio: { ratioName, unit }, ratioIndicator },
  )
}

const getMinMaxRow = (values, unit) => {
  return values.reduce((prev, cur) => {
    return {
      ...prev,
      [cur.realYear]: {
        min: unit === '%' ? cur.min * 100 : cur.min,
        max: unit === '%' ? cur.max * 100 : cur.max,
      },
    }
  }, {})
}

export const handleChartData = (payload) => {
  if (!Array.isArray(payload) || !payload.length) return DEFAULT_CHART_DATA

  const result = {}
  const titleById = getTitleById(payload)

  payload.forEach((portfolio) => {
    const { finance_RatioDataItem: ratioData, id } = portfolio
    const values = addRatioCode(ratioData)

    values.forEach(({ min, max, median, realYear, ratioCode, unit }) => {
      const lineKey = getChartKey(id, ratioCode, LINE_KEY)
      const areaKey = getChartKey(id, ratioCode, AREA_KEY)

      if (!result[realYear]) {
        result[realYear] = {
          year: realYear,
          [lineKey]: unit === '%' ? median * 100 : median,
          [areaKey]: [
            unit === '%' ? min * 100 : min,
            unit === '%' ? max * 100 : max,
          ],
        }
      } else {
        result[realYear] = {
          ...result[realYear],
          [lineKey]: unit === '%' ? median * 100 : median,
          [areaKey]: [
            unit === '%' ? min * 100 : min,
            unit === '%' ? max * 100 : max,
          ],
        }
      }
    })
  })

  return { chartData: Object.values(result), titleById }
}

const getTitleById = (payload) => {
  return payload
    .filter((item) => item.name)
    .reduce((prev, cur) => {
      return { ...prev, [cur.id]: cur.name }
    }, {})
}

const addRatioCode = (ratioData) => {
  return ratioData
    .map((item) =>
      item.values.map((value) => ({
        ...value,
        ratioCode: item.ratioCode,
        unit: item.unit,
      })),
    )
    .flat()
}

export const getChartKey = (id, ratioCode, chartKey) =>
  `${id}-${ratioCode}-${chartKey}`
